import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../lib/cart';
import { useAuth } from '../lib/auth';
import { orders as ordersApi, payments, stores as storesApi, promo as promoApi, coins as coinsApi, terms as termsApi, digitalKhata, api } from '../lib/api';
import { toast } from 'sonner';
import { Wallet, CreditCard, IndianRupee, ArrowRight, Home, Truck, User, Mail, Tag, Bike, Sparkles, Coins, ShieldCheck, Video, ScrollText, MessageCircle, BookOpen, Globe } from 'lucide-react';
import AuthModal from '../components/AuthModal';
import PlatformTermsModal from '../components/PlatformTermsModal';
import PaymentBadges from '../components/PaymentBadges';

const methods = [
  { id: 'whatsapp', label: 'Order via WhatsApp', desc: 'Fastest — pre-fills a WhatsApp message to the shop owner. Confirm & pay directly with them.', Icon: MessageCircle },
  { id: 'cod', label: 'Cash on Delivery', desc: 'Pay when your order arrives.', Icon: Wallet },
  { id: 'razorpay', label: 'Razorpay (UPI / Cards / Netbanking)', desc: 'Pay instantly via UPI, RuPay, Visa, Mastercard or netbanking.', Icon: IndianRupee },
  { id: 'stripe', label: 'Credit / Debit Card', desc: 'Visa · Mastercard · Amex · Discover — Verified by Visa & Mastercard SecureCode.', Icon: CreditCard },
  { id: 'paypal', label: 'PayPal (international buyers)', desc: 'Pay from abroad in US dollars with your PayPal account or card.', Icon: Globe },
];

const DELIVERY_METHODS = {
  self_pickup: { label: 'Self pickup from shop', desc: 'Free — visit the shop to collect.', Icon: Home, fee: 'free' },
  shop_delivery: { label: 'Shop home delivery', desc: 'Owner / their delivery boy brings it.', Icon: Truck },
  by_hand_runner: { label: 'By hand (local runner)', desc: 'A neighbour going your way drops it off.', Icon: User },
  dabbawala: { label: 'Dabbawala / Tiffin service', desc: 'Hot food via the village tiffin route.', Icon: Bike },
  courier_dtdc: { label: 'Courier (DTDC / Bluedart)', desc: 'For out-of-village delivery, 2–4 days.', Icon: Mail },
  courier_indiapost: { label: 'India Post / Speed Post', desc: 'Trusted government parcel, 3–7 days.', Icon: Mail },
  autorickshaw: { label: 'Auto-rickshaw / Tempo', desc: 'For heavy goods (fans, pumps).', Icon: Bike },
};

export default function Checkout() {
  const cart = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [method, setMethod] = useState('cod');
  const [authOpen, setAuthOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const redirectingRef = useRef(false);
  const [addr, setAddr] = useState({ full_name: '', phone: '', line1: '', village: cart.store?.village || '', landmark: '' });
  const [storeFull, setStoreFull] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState('shop_delivery');
  const [promo, setPromo] = useState(null);
  const [coinSummary, setCoinSummary] = useState(null);
  const [coinsToUse, setCoinsToUse] = useState(0);
  const [redeemPreview, setRedeemPreview] = useState({ coins_used: 0, discount_inr: 0, max_redeemable: 0 });
  const [terms, setTerms] = useState(null);          // {version, must_accept, ...}
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [khataEligibility, setKhataEligibility] = useState(null);
  const [prescriptionUrl, setPrescriptionUrl] = useState('');
  const [rxUploading, setRxUploading] = useState(false);
  // Detect if this cart has any prescription-required items (from cart.store OR items)
  const needsPrescription = useMemo(
    () => (cart.items || []).some(i => i.prescription_required),
    [cart.items],
  );
  // Medical shop → require the Compliance Audit checkbox pair (batch/expiry
  // verification + Hyderabad-jurisdiction acceptance).
  const isMedicalCart = cart.store?.category === 'medical';
  const [medBatchVerified, setMedBatchVerified] = useState(false);
  const [medJurisdictionAccepted, setMedJurisdictionAccepted] = useState(false);
  const [paypalEnabled, setPaypalEnabled] = useState(false);

  useEffect(() => {
    payments.methods().then(d => {
      const list = d.methods || d.items || d;
      setPaypalEnabled(Array.isArray(list) && list.some(m => m.id === 'paypal' && m.enabled));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (cart.store?.id && user) {
      digitalKhata.eligibility(cart.store.id).then(setKhataEligibility).catch(() => setKhataEligibility({ eligible: false }));
    }
  }, [cart.store?.id, user]);

  useEffect(() => {
    if (cart.store?.id) {
      storesApi.get(cart.store.id).then(d => {
        setStoreFull(d.store);
        const methods = d.store.delivery_methods || ['shop_delivery'];
        if (!methods.includes(deliveryMethod)) setDeliveryMethod(methods[0]);
      });
    }
    // eslint-disable-next-line
  }, [cart.store?.id]);

  useEffect(() => {
    if (!redirectingRef.current && cart.items.length === 0) nav('/cart');
  }, [cart.items.length, nav]);

  // Fetch promo + coin balance when subtotal changes
  useEffect(() => {
    if (!user || cart.items.length === 0) return;
    promoApi.firstOrder(cart.totals.subtotal).then(setPromo).catch(() => {});
    coinsApi.me().then(setCoinSummary).catch(() => {});
    termsApi.platform().then(t => {
      setTerms(t);
      // Already accepted current version → pre-tick (still visible to the customer)
      if (!t.must_accept) setTermsAgreed(true);
    }).catch(() => {});
  }, [user?.id, cart.totals.subtotal, cart.items.length]);

  // Preview redeem amount whenever user adjusts the slider
  useEffect(() => {
    if (!user || cart.items.length === 0) return;
    coinsApi.redeemPreview(cart.totals.subtotal, coinsToUse).then(setRedeemPreview).catch(() => {});
  }, [user?.id, cart.totals.subtotal, coinsToUse, cart.items.length]);

  if (cart.items.length === 0) return null;

  const availableDelivery = (storeFull?.delivery_methods || ['shop_delivery']);
  const isPickup = deliveryMethod === 'self_pickup';
  const baseDelivery = isPickup ? 0 : cart.totals.delivery;
  const promoDiscount = (promo?.eligible ? promo.discount : 0) || 0;
  const coinDiscount = redeemPreview.discount_inr || 0;
  const displayTotal = Math.max(0, cart.totals.subtotal + baseDelivery - promoDiscount - coinDiscount);

  const hasHighValueItem = cart.items.some(i => Number(i.product.price) >= 10000);
  const otpHint = cart.totals.subtotal >= 2000 || hasHighValueItem;
  const videoHint = cart.totals.subtotal >= 5000;

  const placeOrder = async () => {
    if (!user) { setAuthOpen(true); return; }
    if (!addr.full_name || !addr.phone || !addr.line1 || !addr.village) { toast.error('Please fill in delivery address'); return; }
    if (!termsAgreed) { toast.error('Please accept the Platform Terms & Conditions to continue'); return; }
    setBusy(true);
    try {
      // Record platform T&C acceptance (idempotent on backend) before placing order
      if (terms?.must_accept) {
        try {
          await termsApi.acceptPlatform();
        } catch (e) {
          toast.error('Could not record terms acceptance. Please try again.');
          setBusy(false);
          return;
        }
      }
      if (needsPrescription && !prescriptionUrl) {
        toast.error('Please upload your doctor\'s prescription — one or more items require it.');
        setBusy(false);
        return;
      }
      if (isMedicalCart && !(medBatchVerified && medJurisdictionAccepted)) {
        toast.error('Please tick the batch-verification and Hyderabad-jurisdiction confirmations for medical orders.');
        setBusy(false);
        return;
      }
      const order = await ordersApi.create({
        store_id: cart.store.id,
        items: cart.items.map(i => ({ product_id: i.product.id, quantity: i.qty })),
        address: addr,
        payment_method: method,
        delivery_method: deliveryMethod,
        coins_to_redeem: coinsToUse,
        prescription_url: prescriptionUrl || null,
      });

      // Medical orders → record the compliance confirmation immediately.
      if (isMedicalCart) {
        try {
          const { compliance } = await import('../lib/api');
          await compliance.confirmMedicalOrder({
            order_id: order.id,
            verified_batch_and_expiry: true,
            accepted_jurisdiction: true,
            signature_name: addr.full_name || user?.name || null,
          });
        } catch (e) {
          console.warn('Compliance confirmation failed', e);
        }
      }

      if (method === 'cod') {
        redirectingRef.current = true;
        nav(`/orders/${order.id}`);
        cart.clear();
        toast.success('Order placed successfully!');
        return;
      }
      if (method === 'khata') {
        redirectingRef.current = true;
        cart.clear();
        // Offer to send the auto-generated WhatsApp receipt (with dispute link) to the customer
        if (order.khata_receipt?.wa_url) {
          const openReceipt = window.confirm('✅ Added to your Khata!\n\nWant to receive an instant WhatsApp receipt with the dispute link, in case there\'s any mistake?');
          if (openReceipt) window.open(order.khata_receipt.wa_url, '_blank', 'noopener');
        }
        toast.success('✅ Added to your Khata. Pay the shopkeeper later.');
        nav(`/orders/${order.id}`);
        return;
      }
      if (method === 'whatsapp') {
        // Fetch the pre-filled WhatsApp message and open it in a new tab.
        // The customer's phone opens WhatsApp, they hit Send, and the owner
        // sees the full order + address for one-tap confirmation.
        try {
          const { data } = await api.get(`/orders/${order.id}/whatsapp-link`);
          cart.clear();
          window.open(data.wa_url, '_blank', 'noopener');
          redirectingRef.current = true;
          toast.success("Order created. Owner will confirm on WhatsApp.");
          nav(`/orders/${order.id}`);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Couldn't open WhatsApp — the shop hasn't set a WA number yet.");
        }
        return;
      }
      if (method === 'stripe') {
        const { url } = await payments.stripeCheckout(order.id);
        cart.clear();
        window.location.href = url;
        return;
      }
      if (method === 'paypal') {
        try {
          const pp = await payments.paypalCreate(order.id);
          cart.clear();
          redirectingRef.current = true;
          window.location.href = pp.approve_url;
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'PayPal is not available right now');
        }
        return;
      }
      if (method === 'razorpay') {
        try {
          const rzp = await payments.razorpayOrder(order.id);
          if (!rzp.key_id) { toast.error('Razorpay not configured. Choose another method.'); setBusy(false); return; }
          await openRazorpayCheckout(rzp, order, () => {
            redirectingRef.current = true;
            nav(`/orders/${order.id}`);
            cart.clear();
          });
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Razorpay payment failed');
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not place order');
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-10">
      <h1 className="font-heading font-black text-3xl md:text-4xl">Checkout</h1>
      <div className="mt-6 grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-6">
          <section className="card-soft p-5">
            <div className="font-heading font-bold text-lg">Delivery address</div>
            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <input data-testid="addr-name" placeholder="Full name" value={addr.full_name} onChange={e=>setAddr({...addr, full_name:e.target.value})} className="rounded-lg border border-border bg-white px-3 py-2.5 text-sm"/>
              <input data-testid="addr-phone" placeholder="Phone" value={addr.phone} onChange={e=>setAddr({...addr, phone:e.target.value})} className="rounded-lg border border-border bg-white px-3 py-2.5 text-sm"/>
              <input data-testid="addr-line1" placeholder="House / Street" value={addr.line1} onChange={e=>setAddr({...addr, line1:e.target.value})} className="rounded-lg border border-border bg-white px-3 py-2.5 text-sm md:col-span-2"/>
              <input data-testid="addr-village" placeholder="Village / Town" value={addr.village} onChange={e=>setAddr({...addr, village:e.target.value})} className="rounded-lg border border-border bg-white px-3 py-2.5 text-sm"/>
              <input data-testid="addr-landmark" placeholder="Landmark (optional)" value={addr.landmark} onChange={e=>setAddr({...addr, landmark:e.target.value})} className="rounded-lg border border-border bg-white px-3 py-2.5 text-sm"/>
            </div>
          </section>

          <section className="card-soft p-5">
            <div className="font-heading font-bold text-lg">How will you receive it?</div>
            <p className="text-xs text-ink/60 mt-1">Pick from what this shop supports. Self-pickup is always free.</p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              {availableDelivery.map(m => {
                const def = DELIVERY_METHODS[m] || { label: m, desc: '', Icon: Truck };
                const active = deliveryMethod === m;
                return (
                  <label key={m} data-testid={`delivery-${m}`} className={`cursor-pointer rounded-lg border p-3 flex items-start gap-3 transition ${active?'border-[#E67E22] bg-[#FFF7EE]':'border-border bg-white hover:border-ink/30'}`}>
                    <input type="radio" checked={active} onChange={() => setDeliveryMethod(m)} className="mt-1 accent-[#E67E22]"/>
                    <def.Icon className="w-5 h-5 text-ink/70 mt-0.5 shrink-0"/>
                    <div>
                      <div className="font-semibold text-sm">{def.label} {def.fee === 'free' && <span className="ml-1 text-[10px] text-[#1B5E20] uppercase tracking-wider">Free</span>}</div>
                      <div className="text-xs text-muted-foreground">{def.desc}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            {storeFull?.packaging_options?.length > 0 && (
              <div className="mt-4 rounded-lg bg-[#F6F4EE] border border-border p-3">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 inline-flex items-center gap-1"><Tag className="w-3 h-3"/>How this shop packs your order</div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {storeFull.packaging_options.map(p => (
                    <span key={p} className="pill !text-[10px] !py-1">{({original_pack:'Original pack',paper_wrap:'Paper wrap',polybag:'Polybag',newspaper:'Newspaper roll',cloth_bag:'Cloth bag',byo:'Bring your own bag',tiffin:'Tiffin carrier'}[p]) || p}</span>
                  ))}
                </div>
                <div className="text-[10px] text-ink/50 mt-2">💚 Choose &ldquo;Bring your own bag / container&rdquo; at pickup to support zero-waste.</div>
              </div>
            )}
          </section>

          {/* MandiCoins redemption */}
          {user && (coinSummary?.balance || 0) > 0 && (
            <section data-testid="coin-redeem-section" className="card-soft p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-heading font-bold text-lg inline-flex items-center gap-2"><Coins className="w-5 h-5 text-[#F2A157]"/>Use MandiCoins</div>
                  <p className="text-xs text-ink/60 mt-1">You have <b>{coinSummary.balance}</b> coins · 1 coin = ₹{(coinSummary.coin_value_inr).toFixed(2)} · Max {Math.round(20)}% of subtotal.</p>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-[0.18em] text-ink/50">Discount</div>
                  <div data-testid="coin-discount-amt" className="font-heading font-black text-xl text-[#1B5E20]">– ₹{coinDiscount.toFixed(2)}</div>
                </div>
              </div>
              <input
                data-testid="coin-slider"
                type="range"
                min={0}
                max={redeemPreview.max_redeemable}
                value={Math.min(coinsToUse, redeemPreview.max_redeemable || 0)}
                onChange={e => setCoinsToUse(Number(e.target.value))}
                className="w-full mt-3 accent-[#E67E22]"
              />
              <div className="flex justify-between text-[11px] text-ink/60 mt-1">
                <span>0 coins</span>
                <span>{redeemPreview.coins_used} coins used</span>
                <span>{redeemPreview.max_redeemable} coins (max)</span>
              </div>
            </section>
          )}

          {needsPrescription && (
            <section data-testid="prescription-upload" className="card-soft p-5 border-2 border-[#B71C1C] bg-[#FFEBEE]">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#B71C1C] text-white grid place-items-center">🏥</div>
                <div className="flex-1">
                  <div className="font-heading font-bold text-lg text-[#B71C1C]">Doctor&apos;s prescription required</div>
                  <p className="text-xs text-ink/70 mt-1">One or more items in your cart are Schedule H / H1 medicines and by law require a doctor&apos;s prescription. Please upload a clear photo — the pharmacy will verify it before dispatching.</p>
                  <label className="mt-3 flex items-center gap-2 cursor-pointer rounded-lg border-2 border-dashed border-[#B71C1C] bg-white px-3 py-3">
                    {rxUploading ? (
                      <span className="text-sm">Uploading…</span>
                    ) : prescriptionUrl ? (
                      <>
                        <span className="text-[#1B5E20]">✅</span>
                        <span className="text-sm text-[#1B5E20] flex-1 truncate">Prescription uploaded — tap to replace</span>
                      </>
                    ) : (
                      <>
                        <span className="text-[#B71C1C] text-lg">📄</span>
                        <span className="text-sm text-[#B71C1C] flex-1">Tap to upload prescription (photo or PDF)</span>
                      </>
                    )}
                    <input
                      data-testid="rx-upload-input"
                      type="file"
                      accept="image/*,application/pdf"
                      hidden
                      onChange={async e => {
                        const f = e.target.files?.[0]; if (!f) return;
                        setRxUploading(true);
                        try {
                          const fd = new FormData(); fd.append('file', f);
                          const res = await (await import('../lib/api')).api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                          setPrescriptionUrl(res.data.url || res.data.file_url || res.data.path);
                          toast.success('Prescription uploaded');
                        } catch { toast.error('Upload failed'); }
                        finally { setRxUploading(false); }
                      }}
                    />
                  </label>
                </div>
              </div>
            </section>
          )}

          <section className="card-soft p-5">
            <div className="font-heading font-bold text-lg">Payment method</div>
            <div className="mt-3 space-y-2">
              {methods.filter(m => m.id !== 'paypal' || paypalEnabled).map(m => (
                <label key={m.id} data-testid={`pay-method-${m.id}`} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition ${method===m.id?'border-[#E67E22] bg-[#FFF7EE]':'border-border bg-white hover:border-ink/30'}`}>
                  <input type="radio" name="pay" checked={method===m.id} onChange={() => setMethod(m.id)} className="mt-1 accent-[#E67E22]"/>
                  <m.Icon className="w-5 h-5 text-ink/70"/>
                  <div>
                    <div className="font-semibold text-ink">{m.label}</div>
                    <div className="text-xs text-muted-foreground">{m.desc}</div>
                  </div>
                </label>
              ))}
              {khataEligibility?.eligible && (
                <label data-testid="pay-method-khata" className={`flex items-start gap-3 rounded-lg border-2 p-3 cursor-pointer transition ${method==='khata' ? 'border-[#7A4A00] bg-[#FFF3E0]' : 'border-[#D4B26A]/40 bg-gradient-to-r from-[#FFF8EE] to-white hover:border-[#7A4A00]/50'}`}>
                  <input type="radio" name="pay" checked={method==='khata'} onChange={() => setMethod('khata')} className="mt-1 accent-[#7A4A00]"/>
                  <BookOpen className="w-5 h-5 text-[#7A4A00]"/>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-ink flex items-center gap-2 flex-wrap">
                      Pay on Khata (Udhaar)
                      <span className="text-[9px] uppercase tracking-wider bg-[#1B5E20] text-white px-1.5 py-0.5 rounded-full font-bold">Trusted</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Add this bill to your running balance at <b>{khataEligibility.store_name}</b>. Pay later in cash / UPI.
                    </div>
                    <div className="mt-1.5 text-[11px] text-[#7A4A00]">
                      Current balance: <b>₹{(khataEligibility.current_balance || 0).toFixed(2)}</b>
                      {khataEligibility.credit_limit > 0 && (
                        <> · Available credit: <b>₹{(khataEligibility.available_credit || 0).toFixed(2)}</b> of ₹{khataEligibility.credit_limit.toFixed(0)}</>
                      )}
                      {khataEligibility.credit_limit === 0 && <> · <b>No limit set</b> (unlimited trust)</>}
                    </div>
                  </div>
                </label>
              )}
            </div>
          </section>
        </div>

        <aside className="card-soft p-5 h-fit lg:sticky lg:top-24">
          <div className="font-heading font-bold text-lg">Order summary</div>
          <div className="text-sm text-ink/70 mt-1">From <b>{cart.store.name}</b></div>

          {promo?.eligible && (
            <div data-testid="checkout-promo-banner" className="mt-3 rounded-lg bg-[#FFF4E6] border border-[#F2A157] text-[#8E3B00] p-3 text-xs flex items-start gap-2">
              <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-[#E67E22]"/>
              <div><b>WELCOME100 applied.</b> ₹{promo.discount.toFixed(0)} off — funded by BharatCart.</div>
            </div>
          )}

          <div className="mt-3 space-y-1 text-sm max-h-48 overflow-auto pr-1">
            {cart.items.map(i => (
              <div key={i.product.id} className="flex justify-between"><span className="truncate pr-2">{i.product.name} × {i.qty}</span><span>₹{(i.product.price*i.qty).toFixed(2)}</span></div>
            ))}
          </div>
          <div className="h-px bg-border my-3"/>
          <div className="text-sm space-y-1">
            <div className="flex justify-between"><span>Subtotal</span><span>₹{cart.totals.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Delivery {isPickup && <span className="text-[10px] text-[#1B5E20] uppercase tracking-wider ml-1">Free pickup</span>}</span><span>₹{baseDelivery.toFixed(2)}</span></div>
            {promoDiscount > 0 && (
              <div className="flex justify-between text-[#1B5E20]"><span>WELCOME100</span><span data-testid="ck-promo-line">– ₹{promoDiscount.toFixed(2)}</span></div>
            )}
            {coinDiscount > 0 && (
              <div className="flex justify-between text-[#1B5E20]"><span>MandiCoins ({redeemPreview.coins_used})</span><span data-testid="ck-coin-line">– ₹{coinDiscount.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between font-bold text-lg pt-2"><span>Total</span><span data-testid="checkout-total">₹{displayTotal.toFixed(2)}</span></div>
          </div>

          {(otpHint || videoHint) && (
            <div className="mt-3 space-y-1.5 text-[11px]">
              {otpHint && (
                <div className="rounded-lg bg-[#E8F5E9] border border-[#A5D6A7] text-[#1B5E20] px-2.5 py-2 flex items-start gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
                  <span>Delivery OTP will be auto-generated for this order.</span>
                </div>
              )}
              {videoHint && (
                <div className="rounded-lg bg-[#FFF8E1] border border-[#FFD180] text-[#7A4A00] px-2.5 py-2 flex items-start gap-1.5">
                  <Video className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
                  <span>Returns will require a short video proof.</span>
                </div>
              )}
            </div>
          )}

          <button
            data-testid="place-order-button"
            disabled={busy || !termsAgreed || (isMedicalCart && !(medBatchVerified && medJurisdictionAccepted))}
            onClick={placeOrder}
            className="btn-primary w-full mt-5"
          >{busy ? 'Processing…' : 'Place order'} <ArrowRight className="w-4 h-4"/></button>

          {/* Payment trust strip — Visa · Mastercard · Amex · RuPay · PayPal · UPI */}
          <div className="mt-4 rounded-xl border border-border bg-[#FAFAFA] p-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-2">
              <ShieldCheck className="w-3.5 h-3.5 text-[#1B5E20]"/>Secure payments
            </div>
            <PaymentBadges size="sm" showTrustLine={true}/>
          </div>

          {/* --- Medical Compliance Audit (mandatory for pharmacy carts) --- */}
          {isMedicalCart && (
            <section
              data-testid="medical-compliance-checks"
              className={`mt-3 rounded-lg border-2 p-3 text-[12px] leading-snug ${
                (medBatchVerified && medJurisdictionAccepted)
                  ? 'border-[#A5D6A7] bg-[#F1F8E9]'
                  : 'border-[#B71C1C] bg-[#FFEBEE]'
              }`}
            >
              <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[#B71C1C] mb-2">
                Medical Purchase — Compliance Audit
              </div>
              <label data-testid="med-check-batch-label" className="flex items-start gap-2 mb-2 cursor-pointer">
                <input
                  data-testid="med-check-batch"
                  type="checkbox"
                  checked={medBatchVerified}
                  onChange={(e) => setMedBatchVerified(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#B71C1C] shrink-0"
                />
                <span className="text-ink/85">
                  I have <b>verified</b> that the <b>Batch Number</b> and <b>Expiry Date</b> printed on every medicine strip / bottle I receive will match the details on my bill.
                </span>
              </label>
              <label data-testid="med-check-jurisdiction-label" className="flex items-start gap-2 cursor-pointer">
                <input
                  data-testid="med-check-jurisdiction"
                  type="checkbox"
                  checked={medJurisdictionAccepted}
                  onChange={(e) => setMedJurisdictionAccepted(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#B71C1C] shrink-0"
                />
                <span className="text-ink/85">
                  I understand this purchase is at my <b>sole discretion</b> and agree to the <b>exclusive jurisdiction of the Hyderabad Courts</b> (Telangana, India) for any dispute arising out of this transaction.
                </span>
              </label>
            </section>
          )}

          {/* Mandatory Platform T&C tick-box (Indian business laws + Telangana jurisdiction) */}
          <label data-testid="terms-agree-label" className={`mt-3 flex items-start gap-2 rounded-lg border p-3 text-[12px] leading-snug ${termsAgreed?'border-[#A5D6A7] bg-[#F1F8E9]':'border-[#F2A157] bg-[#FFF7EE]'}`}>
            <input
              data-testid="checkout-terms-check"
              type="checkbox"
              checked={termsAgreed}
              onChange={e => setTermsAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#E67E22] shrink-0"
            />
            <span className="text-ink/80">
              I accept the BharatCart <button type="button" data-testid="checkout-terms-link" onClick={() => setTermsOpen(true)} className="underline font-semibold text-[#D35400] inline-flex items-center gap-1"><ScrollText className="w-3 h-3"/>Platform Terms &amp; Conditions</button>
              {terms?.version && <span className="text-ink/50"> ({terms.version})</span>}
              , including Indian business laws (Consumer Protection Act 2019, DPDP Act 2023, IT Act 2000, GST Act 2017) and exclusive <b>Hyderabad, Telangana</b> jurisdiction. I will not order any illegal or unauthorised goods.
            </span>
          </label>
        </aside>
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen}/>
      <PlatformTermsModal open={termsOpen} onOpenChange={setTermsOpen}/>
    </div>
  );
}

async function openRazorpayCheckout(rzp, order, onSuccess) {
  await new Promise((resolve, reject) => {
    if (document.getElementById('rzp-script')) return resolve();
    const s = document.createElement('script');
    s.id = 'rzp-script';
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });
  // eslint-disable-next-line no-undef
  const r = new window.Razorpay({
    key: rzp.key_id,
    amount: rzp.amount,
    currency: rzp.currency,
    name: 'BharatCart',
    description: order.store_name,
    order_id: rzp.razorpay_order_id,
    handler: async (resp) => {
      try {
        await payments.razorpayVerify({
          order_id: order.id,
          razorpay_order_id: resp.razorpay_order_id,
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_signature: resp.razorpay_signature,
        });
        toast.success('Payment successful!');
        onSuccess();
      } catch (e) { toast.error('Payment verification failed'); }
    },
    prefill: { name: order.address.full_name, contact: order.address.phone },
    theme: { color: '#E67E22' },
  });
  r.open();
}
